import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Search, Loader, PackageSearch, RefreshCw, AlertTriangle, X, Send, ListChecks } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '@libero/core/services/api';
import { filterCategories, CategoryOption } from './categoryFilter';

// =========================================================================
// Types
// =========================================================================

type SearchMode = 'nameContains' | 'name' | 'itemId' | 'variationNumber' | 'ean' | 'category';

interface SearchResultItem {
  itemId: number;
  name: string;
  inPool: boolean;
}

interface PoolDiff {
  toAdd: number[];
  toRemove: number[];
  unchanged: number[];
}

interface PoolItem {
  itemId: number;
  name: string;
}

/** Apply-Lauf-Status wie vom Backend geliefert (poolApply.ts PoolFlagRunStatus). */
type ApplyStatus = 'running' | 'applied' | 'cleaned' | 'failed';

interface ApplyRun {
  status: ApplyStatus;
  writesDone: number;
  writesTotal: number;
  errorMessage?: string | null;
}

const MODE_OPTIONS: { value: SearchMode; label: string }[] = [
  { value: 'nameContains', label: 'Name (enthält)' },
  { value: 'name', label: 'Name (exakt)' },
  { value: 'itemId', label: 'Artikel-ID' },
  { value: 'variationNumber', label: 'Variantennummer' },
  { value: 'ean', label: 'EAN' },
  { value: 'category', label: 'Kategorie' },
];

/** Fehlermeldung aus dem Backend-Envelope defensiv extrahieren (debugging.md). */
function extractErrorMessage(err: any): string {
  return err?.response?.data?.error?.message ?? err?.message ?? 'Unbekannter Fehler';
}

// =========================================================================
// PoolManager — Schritt 2 des Anlage-Ablaufs: Quellartikel suchen + auswählen.
// Beim „Übernehmen" wird flagOne=29 gesetzt (Brücke zu Schritt 3). onProceed
// signalisiert dem Wizard, dass er zu „Farben & Veredelung" weiterspringen kann
// (nach erfolgreichem Apply ODER wenn die Auswahl bereits dem Pool entspricht).
// =========================================================================

interface PoolManagerProps {
  /** Wird aufgerufen sobald die Auswahl übernommen ist (Apply fertig oder keine Änderung nötig). */
  onProceed?: () => void;
}

export default function PoolManager({ onProceed }: PoolManagerProps = {}) {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<SearchMode>('nameContains');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [diff, setDiff] = useState<PoolDiff | null>(null);
  const [diffNames, setDiffNames] = useState<Record<number, string>>({});
  const [searching, setSearching] = useState(false);
  const [computingDiff, setComputingDiff] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Namen ALLER je in Treffern gesehenen Artikel — damit die „Ausgewählte Artikel"-Ansicht
  // auch Artikel beschriften kann, die aus einer früheren Suche stammen (jetzt nicht mehr
  // in `results` sichtbar). Wächst über Suchen hinweg, wird nie geleert.
  const [knownNames, setKnownNames] = useState<Record<number, string>>({});

  // Bestätigungsschritt + Anwenden (WRITE-02): erst nach explizitem "Ja, anwenden" im Dialog
  // wird POST /pool/apply ausgelöst; applying sperrt Button + Dialog gegen Doppel-Apply.
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyRunId, setApplyRunId] = useState<string | null>(null);
  const [applyStatus, setApplyStatus] = useState<ApplyStatus | null>(null);
  const [applyProgress, setApplyProgress] = useState<{ writesDone: number; writesTotal: number } | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const pollIntervalRef = useRef<number | null>(null);

  // Reconciliation (RECON-01): live Artikel, die aktuell noch flagOne=29 tragen.
  const [reconItems, setReconItems] = useState<PoolItem[]>([]);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconLoaded, setReconLoaded] = useState(false);

  // Kategorie-Modus (CAT-01/CAT-02): eigener Typeahead statt des Text-Inputs.
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  // Namen aus der aktuellen Trefferliste, damit toAdd/unchanged auch dann beschriftet sind,
  // wenn ein Artikel bereits aus der Ergebnisliste stammt (Fallback: diffNames vom Backend).
  const resultNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of results) map.set(r.itemId, r.name);
    return map;
  }, [results]);

  const getNameFor = useCallback(
    (itemId: number): string =>
      resultNameById.get(itemId) ?? knownNames[itemId] ?? diffNames[itemId] ?? `Artikel ${itemId}`,
    [resultNameById, knownNames, diffNames],
  );

  const handleSearch = useCallback(async (categoryOverrideId?: number) => {
    const effectiveQuery = mode === 'category'
      ? String(categoryOverrideId ?? selectedCategoryId ?? '')
      : query.trim();
    if (effectiveQuery === '') {
      toast.error(mode === 'category' ? 'Bitte eine Kategorie auswählen' : 'Bitte einen Suchbegriff eingeben');
      return;
    }
    setSearching(true);
    try {
      const raw = await api.get('/programmes/teamshop-artikelanlage/pool/search', {
        params: { query: effectiveQuery, mode },
      });
      const foundResults: SearchResultItem[] = raw.data?.data?.results ?? [];
      setResults(foundResults);
      // Namen persistent merken, damit die Auswahl-Ansicht sie such-übergreifend beschriften kann.
      setKnownNames(prev => {
        const next = { ...prev };
        for (const r of foundResults) next[r.itemId] = r.name;
        return next;
      });
      setHasSearched(true);
      if (foundResults.length === 0) {
        toast('Keine Treffer gefunden');
      }
    } catch (err: any) {
      toast.error(`Suche fehlgeschlagen: ${extractErrorMessage(err)}`);
    } finally {
      setSearching(false);
    }
  }, [query, mode, selectedCategoryId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  // Kategorienliste EINMALIG laden (beim ersten Betreten des Kategorie-Modus), kein Reload
  // bei jedem Tastendruck — clientseitige Filterung uebernimmt filterCategories.
  const loadCategories = useCallback(async () => {
    if (categoriesLoaded || loadingCategories) return;
    setLoadingCategories(true);
    try {
      // ohneTeamshops: hier werden QUELLARTIKEL gesucht — fertige Teamshop-Kategorien waeren
      // eine garantiert leere Trefferliste. Schritt 1 (Wizard) holt dieselbe Liste OHNE diesen
      // Parameter, weil er genau diese Kategorien vorschlagen muss.
      const raw = await api.get('/programmes/teamshop-artikelanlage/pool/categories', {
        params: { ohneTeamshops: true },
      });
      const loaded: CategoryOption[] = raw.data?.data?.categories ?? [];
      setCategories(loaded);
      setCategoriesLoaded(true);
    } catch (err: any) {
      toast.error(`Kategorien laden fehlgeschlagen: ${extractErrorMessage(err)}`);
    } finally {
      setLoadingCategories(false);
    }
  }, [categoriesLoaded, loadingCategories]);

  const handleModeSelect = (value: SearchMode) => {
    setMode(value);
    if (value === 'category') loadCategories();
  };

  const filteredCategorySuggestions = useMemo(
    () => filterCategories(categories, categoryQuery),
    [categories, categoryQuery],
  );

  // Auswahl eines Typeahead-Vorschlags: setzt selectedCategoryId + Label und loest sofort die
  // bestehende Suche (mode='category', query=categoryId) aus — kein zweiter Such-Codepfad.
  const handleSelectCategory = useCallback((cat: CategoryOption) => {
    setSelectedCategoryId(cat.categoryId);
    setCategoryQuery(cat.label);
    setShowCategorySuggestions(false);
    handleSearch(cat.categoryId);
  }, [handleSearch]);

  // Set-Toggle ohne ".delete(" im Quelltext (RO-01-Grep in Task 3 sucht nach
  // schreibenden HTTP-Verben wie ".delete(" — Set.delete() waere ein irrefuehrender Treffer,
  // deshalb ueber filter() rekonstruiert statt Set.prototype.delete zu nutzen).
  const toggleSelected = (itemId: number) => {
    setSelectedIds(prev => {
      if (prev.has(itemId)) {
        return new Set([...prev].filter(id => id !== itemId));
      }
      return new Set(prev).add(itemId);
    });
  };

  // Alle aktuell angezeigten Treffer auf einen Klick an-/abwählen (z.B. eine ganze Kategorie).
  // Kein Set.delete() im Quelltext (RO-01-Grep) — Auswahl per filter()/Spread rekonstruiert.
  const allDisplayedSelected = results.length > 0 && results.every(r => selectedIds.has(r.itemId));
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const displayed = new Set(results.map(r => r.itemId));
      if (results.length > 0 && results.every(r => prev.has(r.itemId))) {
        // alle angezeigten sind gewählt → angezeigte abwählen (übrige Auswahl bleibt erhalten)
        return new Set([...prev].filter(id => !displayed.has(id)));
      }
      // sonst alle angezeigten zur Auswahl hinzufügen
      const next = new Set(prev);
      for (const id of displayed) next.add(id);
      return next;
    });
  };

  const handleComputeDiff = useCallback(async (): Promise<PoolDiff | null> => {
    if (selectedIds.size === 0) {
      toast.error('Bitte mindestens einen Artikel auswählen');
      return null;
    }
    setComputingDiff(true);
    try {
      const raw = await api.post('/programmes/teamshop-artikelanlage/pool/diff', {
        selectedIds: [...selectedIds],
      });
      const body = raw.data?.data ?? {};
      const nextDiff: PoolDiff | null = body.diff ?? null;
      setDiff(nextDiff);
      setDiffNames(body.names ?? {});
      return nextDiff;
    } catch (err: any) {
      toast.error(`Diff-Berechnung fehlgeschlagen: ${extractErrorMessage(err)}`);
      return null;
    } finally {
      setComputingDiff(false);
    }
  }, [selectedIds]);

  // Großer Button oben: berechnet bei Bedarf zuerst die Diff-Vorschau und öffnet dann den
  // Bestätigungsdialog — ein Klick statt „erst Diff, dann Anwenden".
  const handleSelectForTeamshop = useCallback(async () => {
    if (selectedIds.size === 0) {
      toast.error('Bitte mindestens einen Artikel auswählen');
      return;
    }
    const effectiveDiff = diff ?? await handleComputeDiff();
    if (!effectiveDiff) return; // Berechnung fehlgeschlagen (Toast kam schon)
    if (effectiveDiff.toAdd.length + effectiveDiff.toRemove.length === 0) {
      // Auswahl entspricht bereits dem Pool → kein Plenty-Write nötig, direkt weiter.
      toast('Auswahl bereits übernommen — weiter zu Farben & Veredelung');
      onProceed?.();
      return;
    }
    setShowConfirm(true);
  }, [selectedIds, diff, handleComputeDiff, onProceed]);

  // Reconciliation laden (RECON-01) — defensiv über raw.data?.data.items unwrappen.
  const loadReconciliation = useCallback(async () => {
    setReconLoading(true);
    try {
      const raw = await api.get('/programmes/teamshop-artikelanlage/pool/reconciliation');
      const items: PoolItem[] = raw.data?.data?.items ?? [];
      setReconItems(items);
      setReconLoaded(true);
    } catch (err: any) {
      toast.error(`Markierte Artikel laden fehlgeschlagen: ${extractErrorMessage(err)}`);
    } finally {
      setReconLoading(false);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Beim Unmount laufendes Polling immer stoppen (kein Leak über Interval hinaus).
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const pollApplyRun = useCallback(async (runId: string) => {
    try {
      const raw = await api.get(`/programmes/teamshop-artikelanlage/pool/apply/${runId}`);
      const run: ApplyRun | undefined = raw.data?.data?.run;
      if (!run) return;

      setApplyStatus(run.status);
      setApplyProgress({ writesDone: run.writesDone, writesTotal: run.writesTotal });

      if (run.status === 'applied') {
        stopPolling();
        setApplying(false);
        toast.success('Artikel übernommen');
        loadReconciliation();
        // Wizard-Flow: nach erfolgreichem Übernehmen weiter zu „Farben & Veredelung".
        onProceed?.();
      } else if (run.status === 'failed') {
        stopPolling();
        setApplying(false);
        setApplyError(run.errorMessage ?? 'Unbekannter Fehler');
        toast.error(`Pool-Apply fehlgeschlagen: ${run.errorMessage ?? 'Unbekannter Fehler'}`);
      }
      // Status 'running' → weiter pollen (Interval läuft), 'cleaned' wird hier nicht erwartet
      // (folgt erst durch den späteren Auto-Cleanup-Hook, nicht durch diesen Apply-Lauf).
    } catch (err: any) {
      // Poll-Fehler NICHT den laufenden Lauf abbrechen lassen (transientes Netzwerkproblem) —
      // einfach beim nächsten Intervall erneut versuchen, nur loggen statt Toast-Spam.
      // eslint-disable-next-line no-console
      console.error('Pool-Apply Polling fehlgeschlagen:', extractErrorMessage(err));
    }
  }, [stopPolling, loadReconciliation, onProceed]);

  const startPolling = useCallback((runId: string) => {
    stopPolling();
    pollIntervalRef.current = window.setInterval(() => {
      pollApplyRun(runId);
    }, 2000);
  }, [stopPolling, pollApplyRun]);

  // WRITE-02: einziger Auslöser für POST /pool/apply — wird ausschließlich aus dem
  // "Ja, anwenden"-Button im Bestätigungsdialog aufgerufen, nie direkt vom "Pool anwenden"-Button.
  const handleConfirmApply = useCallback(async () => {
    setApplying(true);
    setApplyError(null);
    setApplyStatus('running');
    setApplyProgress(null);
    try {
      const raw = await api.post('/programmes/teamshop-artikelanlage/pool/apply', {
        selectedIds: [...selectedIds],
      });
      const body = raw.data?.data ?? {};
      const runId: string | undefined = body.runId;
      if (!runId) {
        throw new Error('Keine runId in der Antwort');
      }
      setApplyRunId(runId);
      setShowConfirm(false); // Dialog erst bei Erfolg schließen
      startPolling(runId);
    } catch (err: any) {
      const msg = err?.response?.status === 409
        ? 'Es läuft bereits ein Anwenden — kurz warten und erneut versuchen.'
        : err?.response?.status === 403
        ? 'Keine Berechtigung zum Anwenden (Permission view:teamshop-artikelanlage fehlt).'
        : extractErrorMessage(err);
      setApplyError(msg);
      toast.error(`Anwenden fehlgeschlagen: ${msg}`);
      setApplying(false);
      setApplyStatus(null);
      // Dialog bleibt bewusst offen, damit der Fehler sichtbar ist (nicht mehr stumm schließen).
    }
  }, [selectedIds, startPolling]);


  const selectedCount = selectedIds.size;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Anwenden-Fehler dauerhaft sichtbar (nicht nur als flüchtiger Toast) — deckt auch den
          Fall ab, dass der Bestätigungsdialog schon geschlossen war (Polling → 'failed'). */}
      {applyError && applyStatus !== 'running' && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          padding: '12px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
        }}>
          <AlertTriangle size={16} color="#991B1B" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: '0.85rem', color: '#991B1B' }}>
            <strong>Anwenden fehlgeschlagen.</strong> {applyError}
          </div>
        </div>
      )}

      {/* Suche */}
      <div className="card" style={{ margin: 0 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>Quellartikel suchen</h3>
          <button
            className="btn btn-primary"
            onClick={handleSelectForTeamshop}
            disabled={selectedCount === 0 || applying || computingDiff}
            title={selectedCount === 0 ? 'Zuerst Artikel in der Trefferliste auswählen' : ''}
            style={{ padding: '12px 24px', fontSize: '1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {applying
              ? <><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Läuft…</>
              : computingDiff
              ? <><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Berechne Vorschau…</>
              : <><Send size={18} /> Artikel übernehmen & weiter</>
            }
          </button>
        </div>
        <div style={{ padding: '14px 0 4px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {MODE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleModeSelect(opt.value)}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600,
                  cursor: 'pointer',
                  background: mode === opt.value ? '#c8dd40' : '#f3f4f6',
                  color: '#1a1a1a',
                  border: `2px solid ${mode === opt.value ? '#c8dd40' : '#e5e7eb'}`,
                }}>
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {mode === 'category' ? (
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  value={categoryQuery}
                  onChange={e => {
                    setCategoryQuery(e.target.value);
                    setSelectedCategoryId(null);
                    setShowCategorySuggestions(true);
                  }}
                  onFocus={() => setShowCategorySuggestions(true)}
                  onBlur={() => { window.setTimeout(() => setShowCategorySuggestions(false), 150); }}
                  placeholder="Kategorie suchen…"
                  style={{
                    width: '100%', padding: '9px 12px', fontSize: '0.9rem',
                    border: '1px solid #d1d5db', borderRadius: 6,
                  }}
                />
                {showCategorySuggestions && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, marginTop: 4,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                    maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                  }}>
                    {loadingCategories ? (
                      <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: '#9ca3af' }}>Lädt Kategorien…</div>
                    ) : filteredCategorySuggestions.length === 0 ? (
                      <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: '#9ca3af' }}>Keine Kategorie gefunden</div>
                    ) : (
                      filteredCategorySuggestions.map(cat => (
                        <div
                          key={cat.categoryId}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => handleSelectCategory(cat)}
                          style={{ padding: '8px 12px', fontSize: '0.85rem', color: '#1a1a1a', cursor: 'pointer' }}>
                          {cat.label}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  mode === 'nameContains' ? 'Teil des Artikelnamens eingeben (z.B. „Trikot" oder „JAKO Multibag")…'
                  : mode === 'name' ? 'Exakten vollen Artikelnamen eingeben (z.B. „JAKO Multibag One")…'
                  : mode === 'itemId' ? 'Exakte Artikel-ID eingeben…'
                  : mode === 'variationNumber' ? 'Variantennummer eingeben…'
                  : 'EAN eingeben…'
                }
                style={{
                  flex: 1, padding: '9px 12px', fontSize: '0.9rem',
                  border: '1px solid #d1d5db', borderRadius: 6,
                }}
              />
            )}
            <button
              className="btn btn-primary"
              onClick={() => handleSearch()}
              disabled={searching || (mode === 'category' && selectedCategoryId == null)}
              style={{ whiteSpace: 'nowrap' }}>
              {searching
                ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Sucht…</>
                : <><Search size={14} /> Suchen</>
              }
            </button>
          </div>
          <p style={{ margin: 0, fontSize: '0.78rem', color: '#9ca3af' }}>
            {mode === 'category'
              ? 'Kategorie über das Typeahead auswählen — Auswahl startet die Suche automatisch (inkl. Unterkategorien, read-only).'
              : mode === 'nameContains'
              ? 'Freitext-Suche: findet alle Artikel, deren Name den Begriff enthält (mind. 2 Zeichen, case-insensitiv) — schnell über die Artikel-Registry, read-only.'
              : 'Exakter voller Artikelname (case-insensitiv), exakte Artikel-ID oder optional Variantennummer/EAN — Live gegen Plenty (read-only).'}
          </p>
          {/* Der Suchfilter ist sonst unsichtbar: wer einen fertigen Teamshop-Artikel sucht,
              bekaeme eine leere Liste ohne Erklaerung. Regel + Messwerte: poolService.ts
              (FILTER-01) bzw. config.poolSearch.teamshopCategoryRootId. */}
          <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#9ca3af' }}>
            Fertige Teamshop-Artikel (Kategorie „Teamshops") sind ausgeblendet — sie kommen als Quellartikel nicht in Frage.
          </p>
        </div>
      </div>

      {/* Ergebnisliste */}
      <div className="card" style={{ margin: 0 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>
            Treffer
            {results.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: '0.78rem', fontWeight: 400, color: '#6b7280' }}>
                {results.length} gefunden · {selectedCount} ausgewählt
              </span>
            )}
          </h3>
          {results.length > 0 && (
            <button
              onClick={toggleSelectAll}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600,
                cursor: 'pointer', color: '#1a1a1a',
                background: allDisplayedSelected ? '#f3f4f6' : '#fafff0',
                border: `2px solid ${allDisplayedSelected ? '#e5e7eb' : '#c8dd40'}`,
              }}>
              <input
                type="checkbox"
                readOnly
                checked={allDisplayedSelected}
                style={{ width: 15, height: 15, accentColor: '#c8dd40', pointerEvents: 'none' }}
              />
              {allDisplayedSelected ? 'Auswahl aufheben' : `Alle ${results.length} auswählen`}
            </button>
          )}
        </div>
        {results.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af' }}>
            <PackageSearch size={36} style={{ opacity: 0.3, display: 'block', margin: '0 auto 10px' }} />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              {hasSearched ? 'Keine Treffer für diese Suche' : 'Noch keine Suche durchgeführt'}
            </p>
            {hasSearched && (
              <p style={{ margin: '6px 0 0', fontSize: '0.78rem' }}>
                Fertige Teamshop-Artikel werden nicht angezeigt — nach ihnen zu suchen bleibt ergebnislos.
              </p>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {results.map(item => {
              const checked = selectedIds.has(item.itemId);
              return (
                <label
                  key={item.itemId}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '9px 12px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${checked ? '#c8dd40' : '#e5e7eb'}`,
                    background: checked ? '#fafff0' : '#fff',
                  }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelected(item.itemId)}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#c8dd40', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: '0.78rem', color: '#6b7280', minWidth: 70 }}>#{item.itemId}</span>
                  <span style={{ flex: 1, fontSize: '0.88rem', fontWeight: 600, color: '#1a1a1a' }}>{item.name}</span>
                  <span style={{
                    fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                    background: item.inPool ? '#dcfce7' : '#f3f4f6',
                    color: item.inPool ? '#065F46' : '#6b7280',
                    flexShrink: 0,
                  }}>
                    {item.inPool ? '29 gesetzt' : 'kein 29'}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Ausgewählte Artikel — such-übergreifende Sammelansicht aller markierten Artikel.
          Bleibt sichtbar auch wenn man nach weiteren Artikeln sucht (die Treffer-Liste zeigt
          nur die aktuelle Suche). Einzeln entfernbar. */}
      {selectedCount > 0 && (
        <div className="card" style={{ margin: 0 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>
              Ausgewählte Artikel
              <span style={{ marginLeft: 8, fontSize: '0.78rem', fontWeight: 400, color: '#6b7280' }}>
                {selectedCount} markiert
              </span>
            </h3>
            <button
              onClick={() => setSelectedIds(new Set())}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600,
                cursor: 'pointer', color: '#991B1B', background: '#fef2f2',
                border: '2px solid #fca5a5',
              }}>
              Auswahl leeren
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {[...selectedIds].map(itemId => (
              <div
                key={itemId}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 12px', borderTop: '1px solid #f3f4f6',
                }}>
                <span style={{ fontSize: '0.78rem', color: '#6b7280', minWidth: 70 }}>#{itemId}</span>
                <span style={{ flex: 1, fontSize: '0.88rem', fontWeight: 600, color: '#1a1a1a' }}>
                  {getNameFor(itemId)}
                </span>
                <button
                  onClick={() => toggleSelected(itemId)}
                  title="Aus Auswahl entfernen"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                    padding: '4px 10px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600,
                    cursor: 'pointer', color: '#6b7280', background: '#fff',
                    border: '1px solid #e5e7eb',
                  }}>
                  <X size={13} /> Entfernen
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Diff-Vorschau */}
      <div className="card" style={{ margin: 0 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>Diff-Vorschau</h3>
          <button
            className="btn btn-secondary"
            onClick={handleComputeDiff}
            disabled={computingDiff || selectedCount === 0}
            style={{ padding: '6px 14px', fontSize: '0.85rem' }}>
            {computingDiff
              ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Berechnet…</>
              : <><RefreshCw size={14} /> Diff-Vorschau berechnen</>
            }
          </button>
        </div>
        <p style={{ margin: '10px 0 14px', fontSize: '0.8rem', color: '#9ca3af' }}>
          Dry-Run — es wird nichts in Plenty geschrieben.
        </p>

        {!diff ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: '#9ca3af' }}>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Wähle Artikel aus und klicke „Diff-Vorschau berechnen" um zu sehen, was sich ändern würde.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* bekommt 29 */}
            <div style={{ border: '1px solid #86efac', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{
                padding: '8px 14px', background: '#f0fdf4', fontWeight: 700, fontSize: '0.85rem', color: '#065F46',
              }}>
                Bekommt 29 ({diff.toAdd.length})
              </div>
              <DiffList ids={diff.toAdd} getName={getNameFor} emptyText="Keine Artikel" />
            </div>

            {/* verliert 29 */}
            <div style={{ border: '1px solid #fca5a5', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{
                padding: '8px 14px', background: '#fef2f2', fontWeight: 700, fontSize: '0.85rem', color: '#991B1B',
              }}>
                Verliert 29 ({diff.toRemove.length})
              </div>
              <DiffList ids={diff.toRemove} getName={getNameFor} emptyText="Keine Artikel" />
            </div>

            {/* bleibt */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{
                padding: '8px 14px', background: '#f9fafb', fontWeight: 700, fontSize: '0.85rem', color: '#374151',
              }}>
                Bleibt ({diff.unchanged.length})
              </div>
              <DiffList ids={diff.unchanged} getName={getNameFor} emptyText="Keine Artikel" />
            </div>
          </div>
        )}
      </div>

      {/* Markierte Artikel (RECON-01) — live Artikel, die aktuell flagOne=29 tragen */}
      <div className="card" style={{ margin: 0 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>Markierte Artikel</h3>
          <button
            className="btn btn-secondary"
            onClick={loadReconciliation}
            disabled={reconLoading}
            style={{ padding: '6px 14px', fontSize: '0.85rem' }}>
            {reconLoading
              ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Lädt…</>
              : <><ListChecks size={14} /> Markierte Artikel anzeigen (aktualisieren)</>
            }
          </button>
        </div>
        <p style={{ margin: '10px 0 14px', fontSize: '0.8rem', color: '#9ca3af' }}>
          Live-Stand aus Plenty — Reste aus abgebrochenen Läufen werden beim nächsten „Pool
          anwenden" automatisch geräumt (nicht mehr ausgewählte Artikel → verliert 29).
        </p>

        {!reconLoaded ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af' }}>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>Noch nicht geladen.</p>
          </div>
        ) : (
          <>
            <p style={{ margin: '0 0 10px', fontSize: '0.85rem', fontWeight: 600, color: '#1a1a1a' }}>
              {reconItems.length} Artikel tragen aktuell 29
            </p>
            {reconItems.length === 0 ? (
              <div style={{ padding: '14px 0', textAlign: 'center', color: '#9ca3af' }}>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>Keine Reste — Pool ist leer.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {reconItems.map(item => (
                  <div
                    key={item.itemId}
                    style={{
                      display: 'flex', gap: 12, padding: '7px 14px', fontSize: '0.85rem',
                      borderTop: '1px solid #f3f4f6',
                    }}>
                    <span style={{ color: '#6b7280', minWidth: 70 }}>#{item.itemId}</span>
                    <span style={{ color: '#1a1a1a', fontWeight: 500 }}>{item.name}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bestätigungsdialog (WRITE-02) — einziger Weg zu POST /pool/apply */}
      {showConfirm && diff && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(26,26,26,0.55)', zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}>
          <div style={{
            background: '#fff', borderRadius: 10, maxWidth: 560, width: '100%',
            maxHeight: '85vh', overflowY: 'auto', padding: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1a1a1a' }}>Pool wirklich anwenden?</h3>
              <button
                onClick={() => setShowConfirm(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}
                aria-label="Schließen">
                <X size={18} />
              </button>
            </div>

            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px',
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 14,
            }}>
              <AlertTriangle size={16} color="#92400e" style={{ flexShrink: 0, marginTop: 1 }} />
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400e' }}>
                Schreibt in die PRODUKTIVE Plenty — nur <code>flagOne</code> wird verändert.
              </p>
            </div>

            {applyError && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px',
                background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, marginBottom: 14,
              }}>
                <AlertTriangle size={16} color="#991B1B" style={{ flexShrink: 0, marginTop: 1 }} />
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#991B1B' }}>{applyError}</p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ border: '1px solid #86efac', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 14px', background: '#f0fdf4', fontWeight: 700, fontSize: '0.85rem', color: '#065F46' }}>
                  Bekommt 29 ({diff.toAdd.length})
                </div>
                <DiffList ids={diff.toAdd} getName={getNameFor} emptyText="Keine Artikel" />
              </div>
              <div style={{ border: '1px solid #fca5a5', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 14px', background: '#fef2f2', fontWeight: 700, fontSize: '0.85rem', color: '#991B1B' }}>
                  Verliert 29 ({diff.toRemove.length})
                </div>
                <DiffList ids={diff.toRemove} getName={getNameFor} emptyText="Keine Artikel" />
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 14px', background: '#f9fafb', fontWeight: 700, fontSize: '0.85rem', color: '#374151' }}>
                  Bleibt ({diff.unchanged.length})
                </div>
                <DiffList ids={diff.unchanged} getName={getNameFor} emptyText="Keine Artikel" />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirm(false)}
                disabled={applying}
                style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmApply}
                disabled={applying}
                style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                {applying
                  ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Wird angewendet…</>
                  : 'Ja, anwenden'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// DiffList — kleine, wiederverwendete Darstellung einer itemId-Liste
// =========================================================================

const DiffList: React.FC<{ ids: number[]; getName: (itemId: number) => string; emptyText: string }> = ({
  ids, getName, emptyText,
}) => {
  if (ids.length === 0) {
    return (
      <div style={{ padding: '10px 14px', fontSize: '0.8rem', color: '#9ca3af' }}>{emptyText}</div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {ids.map(itemId => (
        <div
          key={itemId}
          style={{
            display: 'flex', gap: 12, padding: '7px 14px', fontSize: '0.85rem',
            borderTop: '1px solid #f3f4f6',
          }}>
          <span style={{ color: '#6b7280', minWidth: 70 }}>#{itemId}</span>
          <span style={{ color: '#1a1a1a', fontWeight: 500 }}>{getName(itemId)}</span>
        </div>
      ))}
    </div>
  );
};
